import { useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import { authService } from '../services/authService';
import ComplianceFooter from '../components/shared/ComplianceFooter';
import { useAppSelector } from '../store/hooks';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/portal/dashboard', icon: <DashboardOutlinedIcon fontSize="small" /> },
];

/**
 * Minimal authenticated shell for borrower routes:
 * top bar with logo + nav + user menu, main content, compliance footer.
 */
const BorrowerLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const [anchorElUser, setAnchorElUser] = useState(null);

  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
    handleCloseUserMenu();
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: (t) => t.piper?.appBg ?? '#f5f6f8' }}>
      <AppBar position="sticky" color="inherit" elevation={0}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ gap: 2 }}>
            <Box
              component={RouterLink}
              to="/portal/dashboard"
              sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mr: 2 }}
            >
              <Box component="img" src="/assets/pp-logo.png" alt="Borrower Portal" sx={{ height: 32 }} />
            </Box>

            <Box sx={{ flexGrow: 1, display: 'flex', gap: 0.5 }}>
              {NAV_ITEMS.map(({ label, path, icon }) => {
                const active = location.pathname === path || location.pathname.startsWith(path + '/');
                return (
                  <Button
                    key={path}
                    component={RouterLink}
                    to={path}
                    startIcon={icon}
                    color={active ? 'primary' : 'inherit'}
                    sx={{ fontWeight: active ? 700 : 500 }}
                  >
                    {label}
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ flexGrow: 0 }}>
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36, fontSize: '0.875rem' }}>
                  {user?.name?.charAt(0)?.toUpperCase() || 'B'}
                </Avatar>
              </IconButton>
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar-user"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                disableScrollLock
              >
                {user?.email && (
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <Typography textAlign="center">Logout</Typography>
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          p: { xs: 2, md: 3 },
        }}
      >
        <Container maxWidth="xl" disableGutters sx={{ flex: 1 }}>
          <Outlet />
        </Container>
      </Box>

      <ComplianceFooter />
    </Box>
  );
};

export default BorrowerLayout;
